import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import {
  CurrencyDollar, ChartLineUp, Megaphone, Star, UsersThree,
  GraduationCap, Trophy, Handshake, Rocket, Lightning, Target,
  ArrowRight, Check, CaretDown, CaretUp,
} from '@phosphor-icons/react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div variants={fadeUp} custom={idx} initial="hidden" whileInView="visible" viewport={{ once: true }} className="border-b border-[#E5E5E0]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-6 text-left group" data-testid={`faq-item-${idx}`}>
        <span className="text-lg font-semibold text-[#0A0A0A] pr-8 group-hover:text-[#FF4400] transition-colors">{q}</span>
        {open ? <CaretUp size={20} weight="bold" /> : <CaretDown size={20} weight="bold" />}
      </button>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="pb-6">
          <p className="text-[#525252] text-base leading-relaxed">{a}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function SalesAgentRecruitPage() {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -60]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const venueNames = [
    'The Brown Palace', 'Four Seasons Denver', 'Denver Botanic Gardens',
    'Blanc Denver', 'The Cable Center', 'Wellshire Event Center',
    'Seawell Grand Ballroom', 'Moss Denver', 'The Manor House',
    'Lionsgate Event Center', 'Cielo at Castle Pines', 'The Wright Room',
  ];

  const benefits = [
    { icon: <CurrencyDollar size={24} weight="duotone" className="text-[#FF4400]" />, title: 'Uncapped Commissions', desc: 'Earn 10% on every deal you close. No ceiling. The more you sell, the more you make. Top agents earn $5K+/month.' },
    { icon: <Target size={24} weight="duotone" className="text-[#FF4400]" />, title: 'Eventism Does the Heavy Lifting', desc: 'Our platform prospects, qualifies leads, and drafts outreach emails for you. You focus on closing, not cold calling.' },
    { icon: <Target size={24} weight="duotone" className="text-[#FF4400]" />, title: 'Warm Leads Delivered', desc: 'We generate qualified leads from Denver\'s event industry — corporate planners, wedding coordinators, venue managers. No door knocking.' },
    { icon: <GraduationCap size={24} weight="duotone" className="text-[#FF4400]" />, title: 'Eventism University', desc: 'Free access to our sales training platform. Learn event industry sales, objection handling, and closing strategies from the best.' },
    { icon: <Lightning size={24} weight="duotone" className="text-[#FF4400]" />, title: 'Fast Payouts', desc: 'Get paid every two weeks. Track your earnings, pipeline, and performance in real-time on your personal dashboard.' },
    { icon: <Handshake size={24} weight="duotone" className="text-[#FF4400]" />, title: 'Work Your Own Hours', desc: 'This is 1099 freelance. Work from anywhere, set your own schedule. Whether it\'s your side hustle or your main gig.' },
  ];

  const steps = [
    { num: '01', title: 'Apply in 2 Minutes', desc: 'Fill out a short application. Tell us about your sales experience and why you want to sell in the event industry. No resume needed.' },
    { num: '02', title: 'Get Approved & Trained', desc: 'Once approved, access Eventism University. Complete the onboarding modules to learn our platform, services, and sales playbook.' },
    { num: '03', title: 'Receive Qualified Leads', desc: 'Eventism continuously prospects Denver businesses and event planners. You get warm leads with email sequences already drafted.' },
    { num: '04', title: 'Close Deals, Earn Commissions', desc: 'Convert leads into bookings. Every time a client books through your pipeline, you earn 10% commission. Payouts every two weeks.' },
    { num: '05', title: 'Level Up & Earn More', desc: 'Hit milestones to unlock bonuses, higher commission tiers, and team leadership opportunities. Your growth has no ceiling.' },
  ];

  const faqs = [
    { q: 'Do I need sales experience?', a: 'Some experience helps, but we\'re more interested in hustle and coachability. Eventism University will teach you everything about event industry sales.' },
    { q: 'Is this a full-time position?', a: 'No — it\'s 1099 independent contractor. Work as much or as little as you want. Many of our top agents do this alongside other work.' },
    { q: 'How much can I realistically earn?', a: 'Commission is 10% per closed deal. Average event bookings range from $3K-$15K, so your commission per deal is $300-$1,500. Top agents close 5-10 deals per month.' },
    { q: 'What do I actually sell?', a: 'Eventism\'s full suite of event services — catering, entertainment, staffing, photography, venue partnerships. You\'re selling a complete event solution, not just one thing.' },
    { q: 'How do I get leads?', a: 'Eventism generates and qualifies leads from Denver businesses. You also get access to our CRM and can build your own pipeline. We provide the tools, you provide the hustle.' },
    { q: 'What tools do I get?', a: 'Personal sales dashboard, pre-drafted email sequences, CRM with pipeline tracking, Eventism University training, proposal builder, and real-time commission tracking.' },
    { q: 'How fast do I get paid?', a: 'Bi-weekly payouts. You can track your pending and paid commissions in real-time on your dashboard.' },
    { q: 'Can I recruit other agents?', a: 'As you level up, you can unlock team leadership roles with additional bonuses for agents you mentor.' },
  ];

  return (
    <div className="min-h-screen bg-[#FBFBF9] overflow-hidden" data-testid="sales-agent-recruit-page">
      <Helmet>
        <title>Become an Eventism Sales Agent | Earn Uncapped Commissions in Denver</title>
        <meta name="description" content="Join Eventism as a sales agent. Earn 10% uncapped commissions selling event services in Denver. Qualified leads, free training, and flexible hours. Apply now." />
        <link rel="canonical" href="https://eventism.com/careers/sales-agent" />
      </Helmet>

      {/* ==================== HERO ==================== */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 px-6 md:px-12 lg:px-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] border border-[#FF4400] px-3 py-1.5 mb-6">
              Now Recruiting Sales Agents
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9] text-[#0A0A0A] mb-6 whitespace-pre-line">
              {'Sell Events.\nEarn Big.'}
            </h1>
            <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed mb-4 max-w-xl">
              Join Eventism's sales team and earn <span className="text-[#0A0A0A] font-bold">uncapped 10% commissions</span> selling Denver's most in-demand event services. Eventism handles prospecting — you handle closing.
            </p>
            <p className="text-base text-[#8A8A83] mb-10 max-w-xl">
              Flexible hours. No cold calling. Free training. Real money.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/sales/apply"
                className="inline-flex items-center justify-center gap-2 bg-[#FF4400] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#E63D00] transition-colors"
                data-testid="hero-apply-button"
              >
                Apply Now — It's Free <ArrowRight size={20} weight="bold" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 border border-[#0A0A0A] text-[#0A0A0A] px-8 py-4 font-bold tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
                data-testid="hero-learn-more"
              >
                How It Works
              </a>
            </div>
          </motion.div>

          <motion.div className="relative" style={{ y: heroY }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative z-10">
              <img src="https://images.unsplash.com/photo-1758691736433-4078b93abd72?w=800&q=80" alt="Sales professional in a meeting" className="w-full h-[400px] md:h-[500px] object-cover" loading="eager" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} className="absolute -bottom-8 -left-8 w-48 h-48 md:w-64 md:h-64 z-20 border-4 border-[#FBFBF9] shadow-2xl">
              <img src="https://images.unsplash.com/photo-1758518729829-162d6bf27b5e?w=400&q=80" alt="Team collaboration" className="w-full h-full object-cover" loading="eager" />
            </motion.div>
            <div className="absolute top-8 -right-4 w-24 h-24 bg-[#FF4400] -z-10" />
          </motion.div>
        </div>
      </section>

      {/* ==================== EARNINGS TICKER ==================== */}
      <section className="bg-[#0A0A0A] py-6 border-y border-[#333]" data-testid="earnings-ticker">
        <Marquee speed={40} gradient={false} pauseOnHover>
          {[
            '$1,500 commission on a corporate gala',
            '$1,000 commission on a wedding package',
            '$450 commission on a birthday party',
            '$2,000 commission on a multi-day conference',
            '$600 commission on a holiday party',
            '$1,200 commission on a product launch',
            '$300 commission on a team-building event',
          ].map((text, i) => (
            <span key={i} className="mx-8 text-sm font-bold uppercase tracking-[0.15em] text-[#FF4400]">
              {text} <span className="text-[#333] mx-2">&bull;</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ==================== COMMISSION BREAKDOWN ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="commission-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">The Money</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              10% on every deal. No cap.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { deal: 'Birthday Party', value: '$4,500', commission: '$450', label: 'Your Commission' },
              { deal: 'Corporate Gala', value: '$15,000', commission: '$1,500', label: 'Your Commission' },
              { deal: 'Wedding Package', value: '$10,000', commission: '$1,000', label: 'Your Commission' },
            ].map((ex, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white border border-[#E5E5E0] p-8 text-center hover:shadow-xl transition-shadow" data-testid={`commission-example-${i}`}
              >
                <p className="text-sm font-bold uppercase tracking-[0.1em] text-[#8A8A83] mb-2">{ex.deal}</p>
                <p className="text-lg text-[#525252] mb-1">Deal Value: {ex.value}</p>
                <p className="text-5xl font-black tracking-tighter text-[#FF4400] mb-1">{ex.commission}</p>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#0A0A0A]">{ex.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-[#F0F0EC] border border-[#E5E5E0] p-8 md:p-12 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.1em] text-[#8A8A83] mb-2">Top Agent Monthly Example</p>
            <p className="text-lg text-[#525252] mb-1">8 deals closed at avg. $7,500 each = $60,000 in bookings</p>
            <p className="text-6xl sm:text-7xl font-black tracking-tighter text-[#0A0A0A] mb-2">$6,000</p>
            <p className="text-sm font-bold uppercase tracking-[0.1em] text-[#FF4400]">Monthly Commission</p>
          </motion.div>
        </div>
      </section>

      {/* ==================== BENEFITS ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F0F0EC]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">Why Eventism</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              We set you up to close.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((card, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white border border-[#E5E5E0] p-8 hover:-translate-y-1 hover:shadow-xl hover:border-[#D4D4CE] transition-all duration-300" data-testid={`benefit-card-${i}`}
              >
                <div className="w-12 h-12 bg-[#F0F0EC] flex items-center justify-center mb-5">{card.icon}</div>
                <h3 className="text-xl font-semibold tracking-tight text-[#0A0A0A] mb-3">{card.title}</h3>
                <p className="text-[#525252] leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-16 text-center">
            <Link to="/sales/apply" className="inline-flex items-center gap-2 bg-[#FF4400] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#E63D00] transition-colors" data-testid="benefits-cta">
              Apply to the Sales Team <ArrowRight size={18} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section id="how-it-works" className="bg-[#0A0A0A] text-white py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="strategy-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="lg:sticky lg:top-32 lg:self-start">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">How It Works</span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] mb-6">
                From application<br />to first commission.
              </h2>
              <p className="text-[#8A8A83] text-lg leading-relaxed max-w-md">
                Most agents close their first deal within 30 days. Our prospecting tools and training platform accelerate your path to revenue.
              </p>
            </motion.div>

            <div className="space-y-12">
              {steps.map((step, i) => (
                <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
                  className="relative pl-16 border-l border-[#333]" data-testid={`strategy-step-${i}`}
                >
                  <span className="absolute left-0 -translate-x-1/2 w-8 h-8 bg-[#FF4400] flex items-center justify-center text-xs font-bold text-white">{step.num}</span>
                  <h3 className="text-2xl font-bold tracking-tight mb-3">{step.title}</h3>
                  <p className="text-[#8A8A83] leading-relaxed">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-20 text-center">
            <Link to="/sales/apply" className="inline-flex items-center gap-2 border border-white text-white px-8 py-4 font-bold tracking-wide hover:bg-white hover:text-[#0A0A0A] transition-colors" data-testid="strategy-cta">
              Start Your Application <ArrowRight size={18} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== WHAT YOU SELL ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="what-you-sell-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">Your Product</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              You sell the entire event.
            </h2>
            <p className="text-[#525252] text-lg mt-4 max-w-2xl mx-auto">Not just one service — you sell Eventism's complete event solution. Higher deal values = higher commissions.</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: 'Catering & Chefs', img: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=300&q=80' },
              { name: 'DJs & Music', img: 'https://images.unsplash.com/photo-1583906703597-f0a9a127c18a?w=300&q=80' },
              { name: 'Photography', img: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=300&q=80' },
              { name: 'Bartenders', img: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=300&q=80' },
              { name: 'Entertainment', img: 'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=300&q=80' },
              { name: 'Event Staff', img: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=300&q=80' },
              { name: 'Photo Booths', img: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=300&q=80' },
              { name: 'Venues', img: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=300&q=80' },
            ].map((service, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="relative group overflow-hidden"
              >
                <img src={service.img} alt={service.name} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                  <p className="text-white font-bold text-sm">{service.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== YOUR TOOLKIT ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F0F0EC]" data-testid="toolkit-section">
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">Your Toolkit</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              Everything you need<br />to close deals.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Eventism lead prospecting tools',
              'Pre-written email sequences',
              'Personal sales dashboard',
              'Real-time commission tracking',
              'CRM with pipeline management',
              'Proposal & quote builder',
              'Eventism University training',
              'Direct Slack support channel',
              'Marketing collateral library',
              'Venue & vendor network access',
              'Client analytics & insights',
              'Bi-weekly direct payouts',
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="flex items-center gap-3 p-4 bg-white border border-[#E5E5E0] hover:border-[#FF4400] transition-colors"
              >
                <div className="w-6 h-6 bg-[#FF4400] flex items-center justify-center shrink-0">
                  <Check size={14} weight="bold" className="text-white" />
                </div>
                <span className="font-medium text-[#0A0A0A]">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== VENUE NETWORK ==================== */}
      <section className="py-16 px-6 md:px-12 lg:px-24 border-y border-[#E5E5E0]" data-testid="venue-network">
        <div className="max-w-7xl mx-auto text-center mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A8A83] mb-4">Venue Partners You'll Sell Into</p>
        </div>
        <Marquee speed={30} gradient={false} pauseOnHover>
          {venueNames.map((name, i) => (
            <span key={i} className="mx-6 text-sm font-bold uppercase tracking-[0.15em] text-[#8A8A83]">
              {name} <span className="text-[#D4D4CE] mx-2">&bull;</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">FAQ</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              Common questions.
            </h2>
          </motion.div>
          <div>
            {faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="bg-[#FF4400] py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9] text-white mb-6">
              Ready to earn<br />what you're worth?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto">
              No upfront cost. No inventory. No ceiling. Just your hustle, our platform, and uncapped commissions.
            </p>
            <Link to="/sales/apply" className="inline-flex items-center gap-2 bg-white text-[#FF4400] px-10 py-5 text-lg font-black tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="final-apply-button">
              Apply Now — It's Free <ArrowRight size={22} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <Link to="/" className="text-2xl font-black tracking-tighter">EVENTISM</Link>
            <p className="text-[#8A8A83] text-sm mt-1">Denver's Event Services Platform</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#8A8A83]">
            <Link to="/partners" className="hover:text-white transition-colors">Vendor Partners</Link>
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
